import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { Vehicle } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditVehicleModal } from "@/modals/vehicles/edit-vehicle-modal";

export function RowActions({ cell }: CellContext<Vehicle, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const editVehicleModal = useDisclosure();
	const vehicleId = cell.row.original.id;
	const navigate = useNavigate();
	const asyncModal = useAsyncModal();
	const { citadel } = useCitadel();

	/** Computed */
	// `Vehicle` reaches the whole fleet or none of it, so these ask the rule without a resource:
	// there is no condition one lorry could satisfy that its neighbour could not. Rules still
	// loading leave both entries in the menu and let the server answer, which is the same
	// definitive-denial reading the rest of these surfaces take.
	const canUpdate = citadel ? citadel.can("update", "Vehicle") : true;
	const canDelete = citadel ? citadel.can("delete", "Vehicle") : true;

	/** Functions */
	function handleOnNavigateToContact() {
		const vehicleId = cell.row.original.id;
		if (!vehicleId) return;
		navigate(`/vehicles/${vehicleId}`);
	}

	async function handleOnDeleteVehicle() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			const response = await client.vehicles[":vehicleId"].$delete({
				param: { vehicleId },
			});
			// A delete that reached no row answers a refusal, and the reader has to see it rather than
			// watch the row stay where it is after a silent success.
			await responseHandler(response);
			await refetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/vehicles/${vehicleId}`}>
				<Menu
					items={() => [
						{
							label: t("general.open"),
							icon: icon("Info"),
							onClick: handleOnNavigateToContact,
							cyId: "row-actions-open-button",
						},
						canUpdate
							? {
									label: t("vehicles.edit_vehicle"),
									onClick: editVehicleModal.onOpen,
									icon: icon("Edit"),
								}
							: null,
						canDelete ? "divider" : null,
						canDelete
							? {
									label: t("general.delete"),
									isDanger: true,
									onClick: handleOnDeleteVehicle,
									cyId: "row-actions-delete-button",
									icon: icon("Trash2"),
								}
							: null,
					]}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			<React.Fragment>
				{editVehicleModal.isOpen && (
					<EditVehicleModal
						isOpen={editVehicleModal.isOpen}
						onClose={editVehicleModal.onClose}
						vehicleId={vehicleId}
						onComplete={refetch}
					/>
				)}
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("vehicles.delete_vehicle_confirm_message")}
					/>
				)}
			</React.Fragment>
		</React.Fragment>
	);
}
